import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { setUnauthorizedHandler } from '../api/client';
import { local, STORAGE_KEYS } from '../lib/storage';
import { toast } from '../ui/Toast';

const SessionContext = createContext(null);

/** Query keys under ['me', …] hold per-user data and are dropped when the user changes. */
export const PRIVATE_QUERY_ROOT = 'me';

/**
 * Signed-in user (the login response, including `token`) persisted under the
 * same localStorage key as before, plus control of the sign-in dialog.
 */
export function SessionProvider({ children }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState(() => local.get(STORAGE_KEYS.user));
  const [auth, setAuth] = useState({ open: false, view: 'login', reason: null, email: '' });
  const afterSignIn = useRef(null);

  const dropPrivateQueries = useCallback(
    () => queryClient.removeQueries({ queryKey: [PRIVATE_QUERY_ROOT] }),
    [queryClient]
  );

  const signIn = useCallback(
    (nextUser) => {
      local.set(STORAGE_KEYS.user, nextUser);
      setUser(nextUser);
      dropPrivateQueries();
      setAuth((current) => ({ ...current, open: false }));
      const pending = afterSignIn.current;
      afterSignIn.current = null;
      if (pending) setTimeout(() => pending(nextUser), 0);
    },
    [dropPrivateQueries]
  );

  const signOut = useCallback(() => {
    local.remove(STORAGE_KEYS.user);
    setUser(null);
    dropPrivateQueries();
  }, [dropPrivateQueries]);

  /** Merges profile changes into the stored user (keeps the token). */
  const updateUser = useCallback((patch) => {
    setUser((previous) => {
      if (!previous) return previous;
      const next = { ...previous, ...patch, token: previous.token };
      local.set(STORAGE_KEYS.user, next);
      return next;
    });
  }, []);

  const openAuth = useCallback(({ view = 'login', reason = null, email = '', onSuccess } = {}) => {
    afterSignIn.current = onSuccess || null;
    setAuth({ open: true, view, reason, email });
  }, []);

  const closeAuth = useCallback(() => {
    afterSignIn.current = null;
    setAuth((current) => ({ ...current, open: false }));
  }, []);

  const setAuthView = useCallback((view) => setAuth((current) => ({ ...current, view })), []);

  /** Runs `action` right away when signed in, otherwise after a successful sign-in. */
  const requireAuth = useCallback(
    (action, options = {}) => {
      if (user?.token) action(user);
      else openAuth({ ...options, onSuccess: action });
    },
    [user, openAuth]
  );

  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (!local.get(STORAGE_KEYS.user)) return;
      signOut();
      toast.error('Your session has expired', { description: 'Please sign in again to continue.' });
      openAuth({ view: 'login' });
    });
    return () => setUnauthorizedHandler(null);
  }, [signOut, openAuth]);

  // Signing in or out in another tab updates this one.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === STORAGE_KEYS.user || event.key === null) {
        setUser(local.get(STORAGE_KEYS.user));
        dropPrivateQueries();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [dropPrivateQueries]);

  const value = useMemo(
    () => ({
      user,
      isSignedIn: Boolean(user?.token),
      canAccessHostPortal: Boolean(user?.isHost || user?.has_property_access),
      signIn,
      signOut,
      updateUser,
      auth,
      openAuth,
      closeAuth,
      setAuthView,
      requireAuth,
    }),
    [user, signIn, signOut, updateUser, auth, openAuth, closeAuth, setAuthView, requireAuth]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}
